import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useState } from "react";
import {
  categoriesColors,
  classNames,
  formatSubmissions,
  statusColors,
  workflowStepIndicator
} from "../utils/formatters";

import { API } from "aws-amplify";
import ExportButton from "../components/ExportButtons";
import Pagination from "../components/Pagination";
import SkeletonTable from "../components/SkeletonTable";
import SortIndicator from "../components/SortIndicator";
import Submission from "../types/Submission";
import { authState } from "../state/atoms";

import { useRecoilState } from "recoil";

const PAGE_SIZE = 25;

// show a list of submissions for the selected fund
const FundSubmissions = (): JSX.Element => {
  const navigate = useNavigate();
  const [auth]: [any, any] = useRecoilState(authState);
  const userEmail = auth && auth.attributes && auth.attributes.email;
  const { fundId } = useParams<{ fundId: string }>();
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [fund, setFund] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // paging
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("updatedAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [queryLoading, setQueryLoading] = useState(false);
  const [assignmentsOnly, setAssignmentsOnly] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setQueryLoading(true);
        const params = {
          fundId: fundId,
          page: page,
          pageSize: PAGE_SIZE,
          sortColumn: sortColumn,
          sortDirection: sortDirection,
          search: search,
          assignmentsOnly: assignmentsOnly,
          userEmail: userEmail
        };

        console.log("params", params);
        const submissionsResponse = await API.get(
          "submissions",
          `/submissions`,
          {
            queryStringParameters: params
          }
        );
        console.log("submissions", submissionsResponse);

        const steps = JSON.parse(submissionsResponse.fund.steps);

        // determine if user is admin to allow for showing admin panel
        const admins = JSON.parse(submissionsResponse.fund.admins);
        setIsAdmin(admins.includes(userEmail));

        setFund(submissionsResponse.fund);
        setTotal(submissionsResponse.total);
        setSubmissions(
          formatSubmissions(submissionsResponse.submissions, steps)
        );
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
        setQueryLoading(false);
      }
    })();
  }, [
    fundId,
    userEmail,
    page,
    search,
    sortColumn,
    sortDirection,
    assignmentsOnly
  ]);

  const handleSort = useCallback(
    (columnName: string) => {
      console.log("sort by", columnName, sortDirection);
      setSortDirection(
        sortColumn === columnName
          ? sortDirection === "desc"
            ? "asc"
            : "desc"
          : "asc"
      );
      setSortColumn(columnName);
      setPage(1);
    },
    [sortDirection, sortColumn]
  );

  const handleChangePage = useCallback((newPage: any) => {
    console.log("handleChangePage", newPage);
    setPage(newPage);
    setQueryLoading(true);
  }, []);

  const handleKeyUp = (e: any) => {
    if (e.key === "Enter") {
      // handleBlurSearch(e.target.value);
      setPage(1);
      setSearch(e.target.value);
      setQueryLoading(true);
    }
  };

  const handleBlurSearch = useCallback((newSearch: string) => {
    // setPage(1);
    // setSearch(newSearch);
    // setQueryLoading(true);
  }, []);

  const handleMyAssignmentsChange = (e: any) => {
    console.log("handleMyAssignmentsChange", e.target.checked);
    setPage(1);
    setQueryLoading(true);
    setAssignmentsOnly(e.target.checked);
  };

  return (
    <div className="min-h-full">
      <div className="pb-32">
        <header className="py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-row justify-between">
            <div>
              {" "}
              <h1 className="text-3xl font-bold text-white mb-2">
                {fund ? (
                  fund!["name"]
                ) : (
                  <div className="h-3 w-60 animate-pulse rounded bg-gray-300"></div>
                )}
              </h1>
              <h2 className="text-white text-xl mb-4">Fund #{fundId}</h2>
            </div>
            <div>
              {isAdmin && (
                <div className="flex flex-row">
                  <ExportButton fundId={fundId} exportType={"Quick"} />
                  <ExportButton fundId={fundId} exportType={"Paysheet"} />
                  <ExportButton fundId={fundId} exportType={"Contact"} />
                </div>
              )}
            </div>
          </div>
        </header>
      </div>
      {loading ? (
        <SkeletonTable />
      ) : (
        <>
          <main className="-mt-32 pb-48">
            <div className="max-w-7xl mx-auto pb-12 px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col">
                <div className="mb-5">
                  <h2 className="text-white text-2xl font-bold mb-4">
                    Fund Workflow Process
                  </h2>
                  <div className="flex flex-row flex-nowrap items-start border-gray-200 border w-full bg-white rounded-t-lg  shadow-md">
                    {fund &&
                      JSON.parse(fund!["steps"]).map((step: any, i: number) => {
                        return (
                          <div
                            key={i}
                            title={step.approvers}
                            className={classNames(
                              i < JSON.parse(fund!["steps"]).length - 1 && "",
                              "flex flex-col flex-auto items-center w-16 border-gray-200 py-4"
                            )}
                          >
                            <div
                              className={classNames(
                                step.skipConditions
                                  ? "from-yellow-600 to-yellow-700"
                                  : "from-green-600 to-green-700",
                                "h-8 w-8 bg-gradient-to-b  rounded-full justify-center mb-2 mx-auto flex items-center text-xs text-green-100 font-bold"
                              )}
                            >
                              {step.id}
                            </div>
                            <div>
                              <p className=" text-xs text-center font-bold">
                                {step.name}
                              </p>
                              <p className=" text-xs text-center">
                                {step.groupName}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                  <div className=" bg-gray-50 italic rounded-b-lg w-full text-center py-2 mb-4 text-xs text-yellow-600">
                    <p>
                      * Steps in yellow indicate conditional steps that only
                      apply to certain application types.
                    </p>
                  </div>
                </div>
                <div className="flex flex-row">
                  <div className="grow">
                    <h2 className="text-white text-2xl font-bold mb-2">
                      Submissions
                    </h2>
                  </div>
                  <div className="relative flex items-end mb-2">
                    <div className="flex items-center h-5">
                      <input
                        id="only-mine"
                        name="only-mine"
                        type="checkbox"
                        onChange={handleMyAssignmentsChange}
                        className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 rounded"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="only-mine" className="font-medium text-white">
                        Show My Assignments
                      </label>
                    </div>
                  </div>
                </div>
                <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <td
                              colSpan={6}
                              className="bg-gray-50 border-b border-gray-300"
                            >
                              <Pagination
                                rowCount={submissions.length}
                                pageSize={PAGE_SIZE}
                                selectedPage={page}
                                total={total}
                                location={"header"}
                                handleKeyUp={handleKeyUp}
                                handleBlurSearch={handleBlurSearch}
                                handleChangePage={handleChangePage}
                              />
                            </td>
                          </tr>
                          <tr>
                            <th
                              scope="col"
                              onClick={() => handleSort("applicantName")}
                              className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              <span className="inline">Name</span>
                              {sortColumn === "applicantName" && (
                                <SortIndicator sortDirection={sortDirection} />
                              )}
                            </th>
                            <th
                              scope="col"
                              onClick={() => handleSort("fundName")}
                              className="cursor-pointer flex-row px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              <span className="grow">Fund</span>
                              {sortColumn === "fundName" && (
                                <SortIndicator sortDirection={sortDirection} />
                              )}
                            </th>
                            <th
                              scope="col"
                              onClick={() => handleSort("applicationType")}
                              className="cursor-pointer flex-row px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              <span className="grow">Type</span>
                              {sortColumn === "applicationType" && (
                                <SortIndicator sortDirection={sortDirection} />
                              )}
                            </th>
                            <th
                              scope="col"
                              onClick={() => handleSort("step")}
                              className="cursor-pointer flex-row px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              <span className="grow">Step</span>
                              {sortColumn === "step" && (
                                <SortIndicator sortDirection={sortDirection} />
                              )}
                            </th>
                            <th
                              scope="col"
                              onClick={() => handleSort("status")}
                              className="cursor-pointer flex-row px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              <span className="grow">Status</span>
                              {sortColumn === "status" && (
                                <SortIndicator sortDirection={sortDirection} />
                              )}
                            </th>
                            <th
                              scope="col"
                              onClick={() => handleSort("updatedAt")}
                              className="cursor-pointer flex-row px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              <span className="grow">Updated</span>
                              {sortColumn === "updatedAt" && (
                                <SortIndicator sortDirection={sortDirection} />
                              )}
                            </th>
                          </tr>
                        </thead>
                        {queryLoading ? (
                          <tbody className="bg-white divide-y divide-gray-200 relative">
                            {[...new Array(5)].map((item, i) => (
                              <tr key={i}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="h-2 bg-gray-200 animate-pulse rounded mb-2"></div>
                                  <div className="h-2 bg-gray-100 animate-pulse rounded"></div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="h-2 bg-gray-200 animate-pulse rounded mb-2"></div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="h-2 bg-gray-200 animate-pulse rounded mb-2"></div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <div className="h-2 bg-gray-200 animate-pulse rounded mb-2"></div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <div className="h-2 bg-gray-200 animate-pulse rounded mb-2"></div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <div className="h-2 bg-gray-200 animate-pulse rounded mb-2"></div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        ) : (
                          <tbody className="bg-white divide-y divide-gray-200">
                            {submissions.length ? (
                              submissions.map((submission: Submission) => (
                                <tr
                                  key={submission.submissionId}
                                  onClick={(e) => {
                                    const url =
                                      "/funds/" +
                                      submission.fundId +
                                      "/" +
                                      submission.submissionId;
                                    if (e.ctrlKey) {
                                      window.open(url, "_blank");
                                    } else {
                                      navigate(url);
                                    }
                                  }}
                                  className="hover:bg-gray-50 hover:cursor-pointer"
                                >
                                  <td className="px-6 py-4 w-1/4">
                                    <div className="flex items-center">
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">
                                          {submission.applicantName}
                                        </div>
                                        {submission.victimName && (
                                          <div className="text-gray-500 text-xs whitespace-nowrap">
                                            <span className="italic font-semibold">
                                              Victim:
                                            </span>{" "}
                                            {submission.victimName}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-green-600 underline hover:no-underline">
                                      <Link to={`/funds/${submission.fundId}`}>
                                        {submission.fundName}
                                      </Link>
                                    </div>
                                    <div className="text-sm text-gray-500"></div>
                                  </td>
                                  <td className="px-6 py-4 w-full">
                                    <span
                                      className={`px-2 py-1 inline-flex text-sm leading-5 font-semibold rounded ${categoriesColors[
                                        submission.applicationTypeShort
                                      ]
                                        }`}
                                    >
                                      {submission.applicationType}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 w-1/2 text-sm text-gray-500 whitespace-nowrap">
                                    {workflowStepIndicator(submission)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <span
                                      className={`px-2 py-1 inline-flex text-sm leading-5 font-semibold rounded ${statusColors[submission.status]
                                        }`}
                                    >
                                      {submission.status}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                                    {submission.updatedDate}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr className="p-4 bg-white rounded">
                                <td colSpan={6} className="text-center p-6">
                                  <span className="text-gray-400 text-sm italic">
                                    No Records Found
                                  </span>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        )}

                        <tfoot>
                          <tr>
                            <td colSpan={6} className="bg-gray-50">
                              <Pagination
                                rowCount={submissions.length}
                                pageSize={PAGE_SIZE}
                                selectedPage={page}
                                total={total}
                                location={"footer"}
                                handleKeyUp={handleKeyUp}
                                handleBlurSearch={handleBlurSearch}
                                handleChangePage={handleChangePage}
                              />
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </>
      )}
    </div>
  );
};

export default FundSubmissions;
